/* eslint max-lines: 0 */
import qs from 'query-string';
import { AxiosResponse } from 'axios';
import uniq from 'lodash/uniq';
import get from 'lodash/get';
import set from 'lodash/set';
import { yupResolver } from '@hookform/resolvers/yup';
import { FieldValues, FormState } from 'react-hook-form';
import { isAfter } from 'date-fns';
import {
  ApiErrorCodeMessages,
  ApiErrorCodeTypes,
  LoadingState,
  MobileOperatingSystems,
  ProfileImageSize,
} from 'types/enums';
import { GenericErrorResponse } from 'types/api.types';
import {
  KeySignatureMetadata,
  MajorFlatKeys,
  MajorSharpKeys,
  MinorFlatKeys,
  MinorSharpKeys,
  TimeSignatureMetadata,
} from 'types/song';
import { config, ENVS } from 'config';
import { channels } from 'shared/constants';
import { externalLinks } from 'app/api.config';
import { DDL, Option, Time } from 'types/general.types';
import { AccountConnectionProviderId, ProfileImage } from 'types/profile';
import { GenericObject } from 'types/generic.types';
import { ConnectionStatus, OpenUrlOptions } from './utils.types';

export const getDirtyValues = <FormValues>(
  dirtyFields: FieldValues,
  allValues: FieldValues
): Partial<FormValues> =>
  Object.keys(dirtyFields).reduce((acc: Partial<FormValues>, curr) => {
    if (typeof dirtyFields[curr] === 'object') {
      return {
        ...acc,
        [curr]: getDirtyValues(dirtyFields[curr], allValues[curr]),
      };
    }
    return {
      ...acc,
      [curr]: allValues[curr],
    };
  }, {});

export const omitNull = (el: string | null): el is string => el !== null;

const errorCodes = {
  BAD_REQUEST: 400,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  METHOD_NOT_ALLOWED: 405,
  CONFLICT: 409,
  TOO_MANY_CALLS: 429,
  SERVER_ERROR: 500,
  BAD_GATEWAY: 502,
};

export const getErrorMessages = (resource = 'resource') => ({
  [errorCodes.BAD_REQUEST]:
    'Input data is invalid, Please adjust it and try again',
  [errorCodes.FORBIDDEN]: "You don't have permission to perform this action",
  [errorCodes.NOT_FOUND]: `Requested ${resource} does not exist`,
  [errorCodes.METHOD_NOT_ALLOWED]:
    'Invalid request sent. Please contact support@session.id for assistance',
  [errorCodes.CONFLICT]: `Resource/data (${resource}) conflict`,
  [errorCodes.TOO_MANY_CALLS]:
    'Too many attempts, Please wait a while and try again',
  [errorCodes.SERVER_ERROR]: 'Unexpected server error, Please try again',
  [errorCodes.BAD_GATEWAY]: 'Unexpected server error, Please try again later',
  DEFAULT: 'An unexpected error has occurred, Please try again later',
});

export const isForbiddenError = (e: any) =>
  e.response?.data?.error?.statusCode === 403 ||
  e?.response?.data?.error?.statusReason === ApiErrorCodeTypes.FORBIDDEN;

export const isNotFoundError = (e: any) =>
  e?.response?.data?.error?.statusCode === 404 ||
  e?.response?.data?.error?.statusReason === ApiErrorCodeTypes.ENTITY_NOT_FOUND;

export const isConflictError = (e: any) =>
  e?.response?.data?.error?.statusCode === 409 ||
  e?.response?.data?.error?.statusReason === ApiErrorCodeTypes.CONFLICT;

const handleStatusError = (status: number, resource?: string) => {
  const errorMessages = getErrorMessages(resource);
  const message = errorMessages[status] || errorMessages.DEFAULT;
  return message;
};

export const apiErrorHandler = (
  response?: AxiosResponse<{ error?: GenericErrorResponse }>,
  resource?: string
): string => {
  const errorObj = response?.data?.error;
  const errorCode = errorObj?.errorCode;
  const statusCode = errorObj?.statusCode;
  if (errorCode) {
    return ApiErrorCodeMessages[errorCode as ApiErrorCodeTypes];
  }
  if (statusCode) {
    const status = Number(statusCode || response?.status);
    return handleStatusError(status, resource);
  }

  return ApiErrorCodeMessages.DEFAULT_ERROR_MESSAGE;
};

export const getWebRedirectUrl = (url: string, options?: OpenUrlOptions) => {
  const isFeatureBranch = Boolean(window.location.hash);
  if (options?.isExternal) {
    return url;
  }
  if (isFeatureBranch) {
    return `${window.location.origin}${window.location.pathname}#${url}`;
  }
  if (options?.newTab || options?.newWindow) {
    return `${window.location.origin}/studio${url}`;
  }
  return url;
};

const getElectronRedirectUrl = (url: string, options?: OpenUrlOptions) => {
  if (options?.isExternal) {
    return url;
  }
  if (options?.newTab) {
    return `${config.sessionWebDomain}${url}`;
  }
  return url;
};

export const openURL = (url: string, options?: OpenUrlOptions) => {
  if (config.isElectronBuild) {
    const redirectUrl = getElectronRedirectUrl(url, options);
    window.api.send(channels.OPEN_EXTERNAL_URL, redirectUrl);
    return;
  }
  const redirectUrl = getWebRedirectUrl(url, options);
  if (options?.newTab) {
    window.open(redirectUrl, '_blank')?.focus();
    return;
  }
  if (options?.newWindow) {
    window
      .open(
        redirectUrl,
        '_blank',
        `width=${window.outerWidth},height=${window.outerHeight}`
      )
      ?.focus();
    return;
  }
  window.location.href = redirectUrl;
};

export const shouldFetch = (state: LoadingState) =>
  [LoadingState.ERROR, LoadingState.PRISTINE].includes(state);

export const isLoading = (state: LoadingState) =>
  [LoadingState.PRISTINE, LoadingState.FETCHING].includes(state);

export const nameToInitials = (name: string) =>
  name
    ? `${name.slice(0, 1)}${
        name.split(' ')[1]?.slice(0, 1) || ''
      }`.toUpperCase()
    : name;

export const buildQueryUrl = (
  url: string,
  data: GenericObject<string | string[]> = {}
) => {
  const { origin, pathname, search } = new URL(url.replace(/{.*.}/, ''));
  const currentQuery = qs.parse(search, { arrayFormat: 'bracket' });

  const getQueryParamsKeys = () => {
    const dataQueryKeys = Object.keys(data);
    const currentQueryKeys = Object.keys(currentQuery);
    const replaceableQueryKeys = url.match(/[^{}]+(?=})/g) || [];
    return uniq([
      ...dataQueryKeys,
      ...currentQueryKeys,
      ...replaceableQueryKeys,
    ]);
  };

  const updatedQuery = getQueryParamsKeys().reduce((acc, curr) => {
    if (data[curr]) {
      return { ...acc, [curr]: data[curr] };
    }
    return acc;
  }, {});

  const parsed = qs.stringify(
    {
      ...currentQuery,
      ...updatedQuery,
    },
    {
      skipEmptyString: true,
      encode: true,
      arrayFormat: 'bracket',
    }
  );

  return `${origin}${pathname}${parsed ? `?${parsed}` : ''}`;
};

export const parseJSON = (json?: string) => {
  try {
    return json && JSON.parse(json);
  } catch (e) {
    return undefined;
  }
};

export const isTimeExpired = (iso?: string) =>
  iso && isAfter(new Date(), new Date(iso));

export const getAvatarImage = (
  profileImages?: ProfileImage[],
  size = ProfileImageSize.AVATAR_TINY
) => (profileImages || []).find((el) => el.name === size)?.href;

const isWindowsMobileOperatingSystem = () => {
  const userAgent = navigator.userAgent || navigator.vendor;
  const isWindows = /windows phone/i.test(userAgent);
  return isWindows;
};

const isAndroidMobileOperatingSystem = () => {
  const userAgent = navigator.userAgent || navigator.vendor;
  const isAndroid = /android/i.test(userAgent);
  return isAndroid;
};

const isIOSMobileOperatingSystem = () => {
  const isIOS =
    (/iPhone|iPod/.test(navigator.platform) ||
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)) &&
    !window.MSStream;
  return isIOS;
};

export const getMobileOperatingSystem = () => {
  if (isWindowsMobileOperatingSystem()) {
    return MobileOperatingSystems.WINDOWS;
  }

  if (isAndroidMobileOperatingSystem()) {
    return MobileOperatingSystems.ANDROID;
  }

  if (isIOSMobileOperatingSystem()) {
    return MobileOperatingSystems.IOS;
  }

  return undefined;
};

export const getAppStoreUrl = (os?: MobileOperatingSystems) => {
  if (os === MobileOperatingSystems.ANDROID) {
    return externalLinks.mobileApp.ANDROID_APP_LINK;
  }
  if (os === MobileOperatingSystems.IOS) {
    return externalLinks.mobileApp.IOS_APP_LINK;
  }
  return null;
};

export const emptyStringToUndefined = (value?: string) =>
  value === '' ? undefined : value;

export const emptyStringToNull = (value?: string | null) =>
  value === '' ? null : value;

export const emptyOptionToNull = (option?: Option | null) =>
  option === null ? null : option?.value;

export const defaultToEmptyString = (value?: string) => value || '';

export const trimText = (text: string, maxLength: number) => {
  if (text.length <= maxLength) {
    return text;
  }

  return `${text.substr(0, maxLength)}...`;
};

const wait = (ms = 1000) => new Promise((resolve) => setTimeout(resolve, ms));

export const poll = async <T>(
  callback: () => Promise<AxiosResponse<T>>,
  condition: (result: AxiosResponse<T>) => boolean,
  ms: number,
  maxTries: number
) => {
  let result = await callback();
  if (condition(result)) {
    return result;
  }
  for (let i = 0; i < maxTries; i += 1) {
    // eslint-disable-next-line no-await-in-loop
    await wait(ms);
    // eslint-disable-next-line no-await-in-loop
    result = await callback();
    if (condition(result)) {
      break;
    }
  }
  if (!condition(result)) {
    throw new Error('max tries limit exceeded');
  }
  return result;
};

export const testIpn = (ipnMin: number, ipnManx: number) => (val?: string) => {
  const valNum = Number(val);
  if (val === '') {
    return true;
  }
  if (Number.isNaN(valNum)) {
    return false;
  }
  return valNum >= ipnMin && valNum <= ipnManx;
};

export const isProd = () => config.sessionApiBaseUrl === ENVS.PROD.API;

export const isQa = () => config.sessionApiBaseUrl === ENVS.QA.API;

export const dynamicYupResolver = (values: any, context: any, options: any) => {
  const { yupValidationSchema } = context;
  if (!yupValidationSchema) {
    return { values, errors: {} };
  }
  return yupResolver(yupValidationSchema)(values, context, options);
};

export function parseQueryParams<T>(url: string): T {
  return (qs.parseUrl(url, { parseFragmentIdentifier: true }) as unknown) as T;
}

export const onlyUnique = (value: string, index: number, self: string[]) =>
  self.indexOf(value) === index;

export const getFormattedTimeSignature = (metadata?: TimeSignatureMetadata) => {
  if (!metadata) {
    return undefined;
  }
  return `${metadata.nom}/${metadata.denom}`;
};

export const getFormattedKeySignature = (metadata?: KeySignatureMetadata) => {
  if (!metadata) {
    return undefined;
  }
  const { isMajor, isSharp, signs } = metadata;
  if (isMajor) {
    if (isSharp) {
      return MajorSharpKeys[signs];
    }
    return MajorFlatKeys[signs];
  }
  if (isSharp) {
    return MinorSharpKeys[signs];
  }
  return MinorFlatKeys[signs];
};

export const ddlToOptions = (ddl: DDL, sort = false) => {
  const options = Object.values(ddl).map(({ name, key }) => ({
    value: key,
    title: name,
  }));

  return sort
    ? options.sort((a, b) => a.title.localeCompare(b.title))
    : options;
};

export const dateToTime = (date: Date): Time => ({
  timestamp: Math.round(date.getTime() / 1000),
  iso: date.toISOString(),
});

export const getConnectionStatusMessage = (
  status: ConnectionStatus,
  providerId: AccountConnectionProviderId,
  name?: string
) => {
  if (status === 'successful') {
    return `${name || providerId} connection was added to your profile`;
  }
  if (status === 'not_verified') {
    return `This ${providerId} account has not been verified. Please verify the account through ${providerId} and then try again.`;
  }
  if (status === 'signup_denied') {
    return `Access to ${providerId} account denied. Please verify your details and try again`;
  }
  return `Couldn't add ${providerId} connection. Please try again`;
};

export const objectDeepKeys = (obj: any) =>
  Object.keys(obj)
    .filter((key) => obj[key] instanceof Object && !Array.isArray(obj[key]))
    .map((key) => objectDeepKeys(obj[key]).map((k) => `${key}.${k}`))
    .reduce((x, y) => x.concat(y), Object.keys(obj));

export const getPrice = (amount: number, currency = 'USD') => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    currencyDisplay: 'symbol',
  }).format(amount / 100);
};

export const getValidDirtyValues = <FormValues>(
  errors: FormState<FormValues>['errors'],
  dirtyFields: FormState<FormValues>['dirtyFields'],
  allValues: FieldValues
) => {
  const validDirtyFieldKeys = objectDeepKeys(dirtyFields)
    .reduce((acc, key) => {
      const isDirty = get(dirtyFields, key);

      if (typeof isDirty === 'object' || !isDirty) {
        return acc;
      }

      return [...acc, key];
    }, [])
    .filter((key) => !get(errors, key));

  return validDirtyFieldKeys.reduce((acc, key) => {
    const fieldValue = get(allValues, key);
    const value = set({}, key, fieldValue);
    return {
      ...acc,
      ...value,
    };
  }, {});
};

export const disableScrollingOnce = () => {
  const x = window.scrollX;
  const y = window.scrollY;
  window.onscroll = () => {
    window.scrollTo(x, y);
    window.onscroll = null;
  };
};

export const triggerDownload = (source: Blob | string, fileName?: string) => {
  const url =
    typeof source === 'string' ? source : window.URL.createObjectURL(source);
  const link = document.createElement('a');
  link.href = url;
  const { userAgent } = navigator;
  const isFirefox = userAgent.match(/firefox|fxios/i);
  if (isFirefox) {
    link.setAttribute('target', '_blank');
  }
  link.setAttribute('download', fileName || '');
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

export const extractFilenameFromURL = (href: string) => {
  const url = new URL(href);
  const params = new URLSearchParams(url.search);
  const contentDisposition = params.get('response-content-disposition');
  if (contentDisposition) {
    const fileNameMatch = (contentDisposition as any).match(
      /filename\s?="(.+)"/
    );
    if (fileNameMatch && fileNameMatch.length === 2) return fileNameMatch[1];
  }
  return 'unknown';
};

export const isoToTime: (time: Time) => Time = (time) => ({
  iso: time.iso,
  timestamp: new Date(time.iso).getTime(),
});

export const arrayChunks = (array, chunk_size) =>
  Array(Math.ceil(array.length / chunk_size))
    .fill(null)
    .map((_, index) => index * chunk_size)
    .map((begin) => array.slice(begin, begin + chunk_size));

export const colorWithOpacityOverSpecificColorRGB = (
  color1: [number, number, number],
  color2: [number, number, number],
  opacity: number
) => {
  const r1 = color1[0];
  const r2 = color2[0];
  const g1 = color1[1];
  const g2 = color2[1];
  const b1 = color1[2];
  const b2 = color2[2];
  const r3 = Math.floor(r2 + (r1 - r2) * opacity);
  const g3 = Math.floor(g2 + (g1 - g2) * opacity);
  const b3 = Math.floor(b2 + (b1 - b2) * opacity);
  return `rgb(${r3}, ${g3}, ${b3})`;
};

export const hexToRgb = (hex: string) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? ([
        parseInt(result[1], 16),
        parseInt(result[2], 16),
        parseInt(result[3], 16),
      ] as [number, number, number])
    : null;
};
